// components
import GridPageAbstract from '../abstract/GridPageAbstract';
import { mergeHeadWithStorage } from '../../utils/gridHeadStorage';
import { CURRENCY_RATE_KIND } from '../../constants/currencyRateKind';

export default class GatewaysPage extends GridPageAbstract {
    constructor(props) {
        super(props);
        this.Name = 'publicGateways';
        this.Server = this.props.context.getState().server.publicGateways;
        this.MenuDisplayType = 'menu';
        this.searchFields = ['name'];

        const defaultHead = [
            // The editor puts each group's fields under its heading, and groups only
            // fields next to each other here: keep a group's fields together.
            { id: 'name', required: true, label: 'name', alignRight: false, cell: 'Text', displayGrid: true },
            // Its gateway settings, one row of each kind (Gateway settings): how
            // its payins and payouts start, and where their results come from.
            // Each offers rows of its own kind only, never archived ones — the API
            // refuses either. Several gateways may share a row. No payin or payout
            // row: that direction is refused (a merchant's payin gets 422
            // PAYIN_DISABLED). No callback row: the result comes only from the
            // cabinet's Confirm and Reject.
            {
                id: 'payinSettingsId',
                group: 'settings',
                label: 'payinSettings',
                alignRight: false,
                cell: 'Select',
                from: 'publicGatewaySettings',
                object: 'payinSettings',
                show: 'name',
                optionsFilter: (row) => row.kind === 'payin' && !row.isArchived,
                helperText: 'payinSettings',
                displayGrid: true
            },
            {
                id: 'payoutSettingsId',
                group: 'settings',
                label: 'payoutSettings',
                alignRight: false,
                cell: 'Select',
                from: 'publicGatewaySettings',
                object: 'payoutSettings',
                show: 'name',
                optionsFilter: (row) => row.kind === 'payout' && !row.isArchived,
                helperText: 'payoutSettings',
                displayGrid: true
            },
            {
                id: 'payinCallbackSettingsId',
                group: 'settings',
                label: 'payinCallbackSettings',
                alignRight: false,
                cell: 'Select',
                from: 'publicGatewaySettings',
                object: 'payinCallbackSettings',
                show: 'name',
                optionsFilter: (row) => row.kind === 'payin_callback' && !row.isArchived,
                helperText: 'payinCallbackSettings',
                displayGrid: true
            },
            {
                id: 'payoutCallbackSettingsId',
                group: 'settings',
                label: 'payoutCallbackSettings',
                alignRight: false,
                cell: 'Select',
                from: 'publicGatewaySettings',
                object: 'payoutCallbackSettings',
                show: 'name',
                optionsFilter: (row) => row.kind === 'payout_callback' && !row.isArchived,
                helperText: 'payoutCallbackSettings',
                displayGrid: true
            },
            // Whether the gateway takes a payin (payout) at all — its minimum and
            // maximum amount, say: a gateway rule of that kind, read before any
            // wallet is chosen. A rule of the other kind, or an archived one, is
            // refused on save (TRIGGER_GATEWAY_RULE_KIND_MISMATCH / _ARCHIVED).
            {
                id: 'payinAllowanceRuleId',
                group: 'rules',
                label: 'payinAllowanceRule',
                alignRight: false,
                cell: 'Select',
                from: 'publicGatewayRules',
                object: 'payinAllowanceRule',
                show: 'name',
                optionsFilter: (row) => row.kind === 'payin' && !row.isArchived,
                helperText: 'payinAllowanceRule',
                displayGrid: true
            },
            {
                id: 'payoutAllowanceRuleId',
                group: 'rules',
                label: 'payoutAllowanceRule',
                alignRight: false,
                cell: 'Select',
                from: 'publicGatewayRules',
                object: 'payoutAllowanceRule',
                show: 'name',
                optionsFilter: (row) => row.kind === 'payout' && !row.isArchived,
                helperText: 'payoutAllowanceRule',
                displayGrid: true
            },
            {
                id: 'walletPayinRuleId',
                group: 'rules',
                label: 'walletPayinRule',
                alignRight: false,
                cell: 'Select',
                from: 'publicWalletRules',
                object: 'walletPayinRule',
                show: 'name',
                displayGrid: true
            },
            {
                id: 'walletPayoutRuleId',
                group: 'rules',
                label: 'walletPayoutRule',
                alignRight: false,
                cell: 'Select',
                from: 'publicWalletRules',
                object: 'walletPayoutRule',
                show: 'name',
                displayGrid: true
            },
            {
                id: 'currencyId',
                group: 'money',
                label: 'currency',
                alignRight: false,
                cell: 'Select',
                from: 'publicCurrencies',
                object: 'currency',
                show: 'code',
                displayGrid: true
            },
            // What this gateway's payins and payouts count in on the balance. Must
            // match the balance currency of every terminal routed here, and of any a
            // wallet pins here — the save is refused otherwise
            // (TRIGGER_BALANCE_CURRENCY_MISMATCH). Empty takes each terminal's.
            {
                id: 'balanceCurrencyId',
                group: 'money',
                label: 'balanceCurrency',
                alignRight: false,
                cell: 'Select',
                from: 'publicCurrencies',
                object: 'balanceCurrency',
                show: 'code',
                displayGrid: true
            },
            // The source whose rate converts into the balance currency (Currency
            // sources), and which of its two rates. Both are required once the
            // balance currency differs from the currency.
            {
                id: 'currencyRateSourceId',
                group: 'money',
                label: 'currencyRateSource',
                alignRight: false,
                cell: 'Select',
                from: 'publicCurrencyRateSources',
                object: 'currencyRateSource',
                show: 'name',
                displayGrid: true
            },
            {
                id: 'currencyRateKind',
                group: 'money',
                label: 'currencyRateKind',
                alignRight: false,
                cell: 'Items',
                items: CURRENCY_RATE_KIND,
                displayGrid: true
            },
            // Applied on top of the source's rate, after any inversion: +1.5 converts
            // at 1.5% more, -1.5 at 1.5% less. The source's own rate is untouched;
            // the adjusted one is what each payin and payout freezes as its
            // balanceRate. The API bounds it to -50..50.
            {
                id: 'currencyRateAdjustmentPercent',
                group: 'money',
                label: 'currencyRateAdjustmentPercent',
                alignRight: true,
                cell: 'Percent',
                min: -50,
                max: 50,
                signed: true,
                helperText: 'currencyRateAdjustmentPercent',
                displayGrid: true
            },
            // On: a payin must fit what the owner paid in advance — one that would
            // take the gateway's balance above zero is refused. Needs a balance
            // currency, which the balance is counted in (refused otherwise).
            {
                id: 'advancePaymentsEnabled',
                group: 'money',
                label: 'advancePaymentsEnabled',
                alignRight: false,
                cell: 'Boolean',
                helperText: 'advancePaymentsEnabled',
                displayGrid: true
            },
            // Payment timings, in minutes. Set all three and the gateway serves one
            // payin per wallet at a time and expires unpaid payins at window +
            // grace; leave all three empty and wallets are shared, with the global
            // expiry. The API refuses some without the others. Payins told apart by
            // wallet need them: requisites or tg_bot payin settings, or a payin
            // callback matching by wallet (tg_session, http_rest by wallet amount).
            // Empty, not 0, in the grid: a cooldown of 0 is a real setting.
            ...['paymentWindowMinutes', 'paymentGraceMinutes', 'walletCooldownMinutes'].map((id) => ({
                id,
                group: 'walletSelection',
                label: id,
                alignRight: true,
                cell: 'Number',
                blankWhenNull: true,
                helperText: id,
                displayGrid: true
            })),
            // With the timings: one payin per wallet, or many told apart by amount,
            // each paid only by exactly its payable amount. distinct_amounts needs
            // the timings; the API refuses it without them. The items are listed
            // here, not in src/constants: their labels are grouped (enumGroup), and
            // the i18n check asks a flat enums.<value> of every constants array.
            {
                id: 'walletPayinMode',
                group: 'walletSelection',
                label: 'walletPayinMode',
                alignRight: false,
                cell: 'Items',
                items: ['one_payin', 'distinct_amounts'],
                enumGroup: 'walletPayinMode',
                helperText: 'walletPayinMode',
                displayGrid: true
            },
            // For distinct_amounts: when the amount is open on every wallet, move it
            // a step at a time, at most max away, in minor units. All three or none;
            // none refuses such a payin, so the direction can be cleared.
            {
                id: 'amountNudgeDirection',
                group: 'walletSelection',
                label: 'amountNudgeDirection',
                alignRight: false,
                cell: 'Items',
                items: ['up', 'down', 'both'],
                enumGroup: 'amountNudgeDirection',
                clearable: true,
                helperText: 'amountNudgeDirection',
                displayGrid: true
            },
            ...['amountNudgeStep', 'amountNudgeMax'].map((id) => ({
                id,
                group: 'walletSelection',
                label: id,
                alignRight: true,
                cell: 'Number',
                blankWhenNull: true,
                helperText: id,
                displayGrid: true
            })),
            // The shape every wallet of the gateway gives its receiverInfo: the
            // wallet editor shows that template's fields, and the cabinet refuses
            // anything else. Taken only while the gateway's wallets fit it; empty
            // for any object. The fields themselves come from the API.
            {
                id: 'walletRequisitesTemplate',
                group: 'walletSelection',
                label: 'walletRequisitesTemplate',
                alignRight: false,
                cell: 'Items',
                items: ['card', 'phone', 'crypto'],
                enumGroup: 'walletRequisitesTemplate',
                clearable: true,
                helperText: 'walletRequisitesTemplate',
                displayGrid: true
            },
            // From public.gateway_balances, in the balance currency: what the owner
            // owes, negative when prepaid — an advance is this below zero. Only
            // gateways have a balance; wallets have takings. Computed per page, so
            // neither sortable nor filterable, and never sent back.
            {
                id: 'balance.total',
                label: 'gatewayBalance',
                hint: 'helperTexts.gatewayBalance',
                alignRight: true,
                cell: 'Amount',
                object: 'balance',
                nestedObject: 'total',
                currencyObject: 'balance',
                displayGrid: true,
                disableEditor: true,
                disableSort: true
            },
            { id: 'isActive', group: 'status', label: 'isActive', alignRight: false, cell: 'Enable', displayGrid: true },
            { id: 'isArchived', group: 'status', label: 'isArchived', alignRight: false, cell: 'Boolean', displayGrid: true },
            {
                id: 'modifiedReason',
                label: 'modifiedReason',
                alignRight: false,
                cell: 'BigText',
                displayGrid: true,
                disableEditor: true
            },
            {
                id: 'modifiedBy',
                label: 'modifiedBy',
                alignRight: false,
                cell: 'Select',
                object: 'modifiedBy',
                show: 'firstName+lastName',
                displayGrid: true,
                disableEditor: true,
                customSort: 'modifiedBy.firstName'
            },
            {
                id: 'dateCreatedModified',
                elements: ['dateCreated', 'dateModified'],
                label: 'dateCreatedModified',
                alignRight: false,
                cell: 'CreateModify',
                disableEditor: true,
                displayGrid: true,
                customSort: 'dateCreated'
            }
        ];
        this.Head = mergeHeadWithStorage(this.Name, defaultHead);

        this.state = {
            ...this.state,
            head: this.Head
        };
    }
}
